import { useEffect } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button, Input } from '@iveco-ti/iveco-ds'
import { FormProvider, useForm } from '@iveco-ti/iveco-ds/hook-form'
import { useTranslation } from 'react-i18next'

import { JsonInput } from '@/common/components/JsonInput'
import { ApplicationAuthModel } from '@/common/models/application'
import { useCreateApplicationAuth } from '../hooks/useCreateApplicationAuth'
import { useExecuteAuth } from '../hooks/useExecuteAuth'
import { useUpdateApplicationAuth } from '../hooks/useUpdateApplicationAuth'
import {
  ManageAuthCreateForm as ManageAuthCreateFormType,
  ManageAuthCreateSchema,
  ManageAuthUpdateForm as ManageAuthUpdateFormType,
  ManageAuthUpdateSchema,
} from '../schemas/ManageAuthSchema'

interface ManageAuthFormProps {
  application: {
    id: string
  } | null
  authData: ApplicationAuthModel | null | undefined
  hasExistingAuth: boolean
  onStartEditCredentials: () => void
  onClose: () => void
  forceReset?: boolean
}

export function ManageAuthForm({
  application,
  authData,
  hasExistingAuth,
  onStartEditCredentials,
  onClose,
  forceReset = false,
}: ManageAuthFormProps) {
  const { t } = useTranslation(['sso', 'common'])

  const { handleCreateAuth } = useCreateApplicationAuth()
  const { handleUpdateAuth } = useUpdateApplicationAuth()
  const { handleExecuteAuth, isExecutingAuth } = useExecuteAuth()

  const authCreateForm = useForm<ManageAuthCreateFormType>({
    resolver: zodResolver(ManageAuthCreateSchema()),
    defaultValues: {
      clientKey: '',
      password: '',
      headers: '',
    },
  })

  const authUpdateForm = useForm<ManageAuthUpdateFormType>({
    resolver: zodResolver(ManageAuthUpdateSchema()),
    defaultValues: {
      headers: '',
    },
  })

  const handleSubmitCreateAuth = async (data: ManageAuthCreateFormType) => {
    if (!application) return

    const createData = {
      applicationId: application.id,
      subId: null,
      clientKey: data.clientKey,
      secretData: {
        password: data.password,
      },
      headers: data.headers.trim() ? JSON.parse(data.headers) : {},
      metadata: null,
    }
    await handleCreateAuth(createData)
    onClose()
  }

  const handleSubmitUpdateAuth = async (data: ManageAuthUpdateFormType) => {
    if (!authData) return

    const updateData = {
      id: authData.id,
      applicationId: authData.applicationId,
      subId: authData.subId,
      clientKey: authData.clientKey,
      secretData: authData.secretData,
      headers: data.headers.trim() ? JSON.parse(data.headers) : {},
      metadata: authData.metadata,
    }
    await handleUpdateAuth(updateData)
    onClose()
  }

  useEffect(() => {
    authCreateForm.reset({
      clientKey: '',
      password: '',
      headers: '',
    })

    authUpdateForm.reset({
      headers: '',
    })

    if (hasExistingAuth && authData?.id && !forceReset) {
      authUpdateForm.reset({
        headers: authData.headers
          ? JSON.stringify(authData.headers, null, 2)
          : '',
      })
    }
  }, [authData, authCreateForm, authUpdateForm, hasExistingAuth, forceReset])

  useEffect(() => {
    return () => {
      authCreateForm.reset({
        clientKey: '',
        password: '',
        headers: '',
      })
      authUpdateForm.reset({
        headers: '',
      })
    }
  }, [authCreateForm, authUpdateForm])

  if (!hasExistingAuth) {
    return (
      <FormProvider {...authCreateForm}>
        <form
          id="manage-auth-form"
          className="space-y-6"
          onSubmit={authCreateForm.handleSubmit(handleSubmitCreateAuth)}
          autoComplete="new-password"
        >
          <Input.Base
            name="clientKey"
            label={t(
              'sso:applications.drawer_manage_auth.fields.client_key.label',
            )}
            placeholder={t(
              'sso:applications.drawer_manage_auth.fields.client_key.placeholder',
            )}
            autoComplete="new-password"
          />

          <Input.Base
            name="password"
            type="password"
            label={t(
              'sso:applications.drawer_manage_auth.fields.password.label',
            )}
            placeholder={t(
              'sso:applications.drawer_manage_auth.fields.password.placeholder',
            )}
            autoComplete="new-password"
          />

          <JsonInput
            name="headers"
            label={t(
              'sso:applications.drawer_manage_auth.fields.headers.label',
            )}
            placeholder={t(
              'sso:applications.drawer_manage_auth.fields.headers.placeholder',
            )}
          />
        </form>
      </FormProvider>
    )
  }

  return (
    <FormProvider {...authUpdateForm}>
      <form
        id="manage-auth-form"
        className="space-y-6"
        onSubmit={authUpdateForm.handleSubmit(handleSubmitUpdateAuth)}
        autoComplete="new-password"
      >
        <Input.Base
          name="clientKey"
          label={t(
            'sso:applications.drawer_manage_auth.fields.client_key.label',
          )}
          value={authData?.clientKey || ''}
          disabled
          readOnly
        />

        <Input.Base
          name="password"
          type="password"
          label={t('sso:applications.drawer_manage_auth.fields.password.label')}
          value="********"
          disabled
          readOnly
        />

        <JsonInput
          name="headers"
          label={t('sso:applications.drawer_manage_auth.fields.headers.label')}
          placeholder={t(
            'sso:applications.drawer_manage_auth.fields.headers.placeholder',
          )}
        />

        <div className="flex gap-3 justify-between">
          <Button.Root
            type="button"
            variant="secondary"
            color="brand"
            onClick={onStartEditCredentials}
          >
            {t(
              'sso:applications.drawer_manage_auth.edit_credentials_button.label',
            )}
          </Button.Root>

          <Button.Root
            type="button"
            shape="pill"
            variant="secondary"
            color="warning"
            onClick={() => handleExecuteAuth(application?.id || '')}
            isLoading={isExecutingAuth}
          >
            {t('sso:applications.drawer_manage_auth.execute_auth_button.label')}
          </Button.Root>
        </div>
      </form>
    </FormProvider>
  )
}
